// @ts-check
import { EChart, LegendIcon } from '/b2b/common/components/Charts';
import { MenuItem, Stack } from '@mui/material';
import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    axisNameForTimeFormat,
    getTimeFormat,
    maxTimeInterval,
    timeLabelFormatter,
} from '/b2b/common/components/Charts/helpers';

import { CMP_CONSENT_CATEGORIES } from '/b2b/consent/constants';
import ConsentDashboardContext from '../ConsentDashboard.context';
import Locale from '/b2b/common/components/Locale';
import PropTypes from 'prop-types';
import Select from '/b2b/common/components/FormControls/Select';
import WidgetContainer from '/b2b/common/components/Reporting/WidgetContainer';
import { escapeCSVValue } from '/b2b/common/helpers/CSV';
import { fetchConsentReportsDashboard } from '/b2b/consent/services';
import { formatChartTooltip } from '/b2b/consent/format';
import { makeDownloadBlob } from '/b2b/common/components/Reporting/helpers';
import merge from 'lodash/merge';
import moment from 'moment';
import { parseDate } from '/b2b/common/helpers/Date';
import { translate } from '/b2b/common/helpers/i18n';
import useBoundCallback from '/b2b/common/helpers/Hooks/useBoundCallback';
import { useTheme } from '@mui/material/styles';
import { withAsyncData } from '/b2b/common/hoc/withAsyncData';
import withRetranslate from '/b2b/common/hoc/withRetranslate';

export const REPORT_NAME = 'optInOut';

/** @type {import('echarts').SeriesOption} */
const commonSeries = {
    type: 'bar',
    stack: 'total',
    barMinHeight: 1,
    label: {
        show: false,
    },
    emphasis: {
        focus: 'series',
    },
};

/** @type {import('echarts').EChartsOption & { series: import('echarts').SeriesOption[] }} */
const staticOptions = {
    legend: {
        icon: LegendIcon,
        orient: 'horizontal',
        top: 'top',
    },
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            type: 'none',
        },
        appendTo: document.body,
    },
    grid: {
        containLabel: false,
    },
    useUTC: true,
    xAxis: {
        axisLabel: {
            hideOverlap: true,
            showMaxLabel: false,
            showMinLabel: false,
        },
        axisLine: { onZero: false, show: true },
        axisTick: { show: false },
        minorTick: { show: false },
        nameGap: 32,
        nameLocation: 'middle',
        nameTextStyle: {
            verticalAlign: 'top',
        },
        splitLine: { show: false },
        type: 'time',
    },
    yAxis: {
        axisLabel: { align: 'right', margin: 0, showMinLabel: false },
        axisLine: { show: false },
        axisTick: { show: false },
        nameLocation: 'middle',
        nameRotate: 90,
        nameTextStyle: {
            verticalAlign: 'bottom',
        },
        splitLine: { show: true },
        splitNumber: 2,
        type: 'value',
    },
    series: [
        {
            ...commonSeries,
            id: 'OptIn',
            name: 'common.optIn',
        },
        {
            ...commonSeries,
            id: 'OptOut',
            name: 'common.optOut',
        },
    ],
};

const csvColumnMap = {
    date: 'common.date',
    [CMP_CONSENT_CATEGORIES.OPT_OUT + 'OptIn']: [
        `consent.categories.${CMP_CONSENT_CATEGORIES.OPT_OUT}`,
        'common.optIn',
    ],
    [CMP_CONSENT_CATEGORIES.OPT_OUT + 'OptOut']: [
        `consent.categories.${CMP_CONSENT_CATEGORIES.OPT_OUT}`,
        'common.optOut',
    ],
    [CMP_CONSENT_CATEGORIES.ANALYTICS + 'OptIn']: [
        `consent.categories.${CMP_CONSENT_CATEGORIES.ANALYTICS}`,
        'common.optIn',
    ],
    [CMP_CONSENT_CATEGORIES.ANALYTICS + 'OptOut']: [
        `consent.categories.${CMP_CONSENT_CATEGORIES.ANALYTICS}`,
        'common.optOut',
    ],
    [CMP_CONSENT_CATEGORIES.PERSONALIZATION + 'OptIn']: [
        `consent.categories.${CMP_CONSENT_CATEGORIES.PERSONALIZATION}`,
        'common.optIn',
    ],
    [CMP_CONSENT_CATEGORIES.PERSONALIZATION + 'OptOut']: [
        `consent.categories.${CMP_CONSENT_CATEGORIES.PERSONALIZATION}`,
        'common.optOut',
    ],
    [CMP_CONSENT_CATEGORIES.MARKETING + 'OptIn']: [
        `consent.categories.${CMP_CONSENT_CATEGORIES.MARKETING}`,
        'common.optIn',
    ],
    [CMP_CONSENT_CATEGORIES.MARKETING + 'OptOut']: [
        `consent.categories.${CMP_CONSENT_CATEGORIES.MARKETING}`,
        'common.optOut',
    ],
    // Essential is intentionally excluded from the chart (no dropdown option), but
    // included in the CSV export.
    [CMP_CONSENT_CATEGORIES.ESSENTIAL + 'OptIn']: [
        `consent.categories.${CMP_CONSENT_CATEGORIES.ESSENTIAL}`,
        'common.optIn',
    ],
    [CMP_CONSENT_CATEGORIES.ESSENTIAL + 'OptOut']: [
        `consent.categories.${CMP_CONSENT_CATEGORIES.ESSENTIAL}`,
        'common.optOut',
    ],
};

/**
 * @param {Unpacked<CmpReportsData['consentsOverTime']>} row
 * @param {Array<keyof typeof csvColumnMap>} columnNames
 * @return {(string|number)[]}
 */
const processRow = (row, columnNames) => {
    return columnNames.reduce((acc, column) => {
        switch (column) {
            case 'date':
                acc.push(moment.utc(parseDate(row?.[column])).format('YYYY-MM-DD'));
                break;
            default:
                acc.push(row?.[column] ?? 0);
                break;
        }
        return acc;
    }, /** @type {(string|number)[]} */ ([]));
};

/**
 * @param {CmpReportsData} data
 * @returns {Blob}
 */
function makeCSV(data) {
    const { consentsOverTime = [] } = data || { consentsOverTime: undefined };
    const format = getTimeFormat(data.dateRange[0], data.dateRange[1]);
    const columnNames = /** @type {Array<keyof typeof csvColumnMap>} */ (Object.keys(csvColumnMap));
    // String CSV
    const csvFile = consentsOverTime
        .reduce(
            (acc, row) => {
                const rowArray = processRow(row, columnNames);
                acc.push(rowArray.map(escapeCSVValue).join(','));
                return acc;
            },
            [
                columnNames
                    .map(column => {
                        switch (column) {
                            case 'date':
                                return translate(
                                    axisNameForTimeFormat(format) || csvColumnMap[column],
                                    { date: '' }
                                );
                            default:
                                return translate(csvColumnMap[column], { delimiter: ' - ' });
                        }
                    })
                    .map(escapeCSVValue)
                    .join(','),
            ]
        )
        .join('\n');
    return new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
}

/** @typedef {typeof CMP_CONSENT_CATEGORIES[keyof typeof CMP_CONSENT_CATEGORIES]} ConsentCategory */

export const OptInOut = props => {
    const { data: providedData, filters, loading: providedLoading, shouldRetranslate } = props;
    const { data, error, load, loading } = useContext(ConsentDashboardContext);
    /** @type {import('/b2b/common/theme').OsanoDefaultTheme} */
    const theme = useTheme();
    const [category, setCategory] = useState(
        /** @type {ConsentCategory} */ (CMP_CONSENT_CATEGORIES.OPT_OUT)
    );
    const { consentsOverTime = [], dateRange = [], timeConsentRange = [] } = data || {};
    const noResults =
        consentsOverTime.length === 0 ||
        !consentsOverTime.some(
            // @ts-ignore
            ({ [`${category}OptIn`]: optIn, [`${category}OptOut`]: optOut }) =>
                optIn > 0 || optOut > 0
        );
    const isLoading = loading || providedLoading;

    const echartsInstance = useRef(null);
    const onChartReady = useCallback(chart => {
        echartsInstance.current = chart;
    }, []);

    const memoOption = useMemo(() => {
        const minTotal = timeConsentRange[0] || 0;
        let scale = 1;
        if (minTotal >= 1000) {
            scale = 1000;
        } else if (minTotal >= 1000000) {
            scale = 1000000;
        }

        const dates = consentsOverTime.map(({ date }) =>
            moment(parseDate(date)).format('YYYY-MM-DD')
        );
        const format = getTimeFormat(dateRange[0], dateRange[1]);
        return merge(
            {},
            staticOptions,
            /** @type {import('echarts').EChartsOption} */ ({
                legend: {
                    left: theme.spacing(1.5),
                },
                tooltip: {
                    formatter: formatChartTooltip(format),
                },
                grid: {
                    left: theme.spacing(8.5),
                    right: theme.spacing(4.5),
                },
                xAxis: {
                    name: translate(axisNameForTimeFormat(format), { date: '' }).trim(),
                    axisLabel: {
                        formatter: timeLabelFormatter(dateRange[0], dateRange[1]),
                        customValues: dates,
                    },
                    max: dateRange[1],
                    min: dateRange[0],
                    maxInterval: maxTimeInterval(dateRange[0], dateRange[1]),
                },
                yAxis: {
                    show: consentsOverTime.length > 0,
                    name: translate('common.consents'),
                    nameGap: parseInt(theme.spacing(5), 10),
                    axisLabel: {
                        formatter: value => {
                            switch (scale) {
                                case 1000:
                                    return `${value / scale}k`;
                                case 1000000:
                                    return `${value / scale}m`;
                                default:
                                    return value;
                            }
                        },
                    },
                },
                dataset: {
                    source: consentsOverTime,
                },
                series: staticOptions.series.map(series => {
                    return {
                        ...series,
                        name: translate(series.name),
                        dimensions: [
                            { name: 'date', type: 'ordinal' },
                            { name: `${category}${series.id}`, type: 'int' },
                        ],
                    };
                }),
            })
        );
    }, [consentsOverTime, shouldRetranslate, category]);

    /** @type {() => Promise<CmpReportsResponseData>} */
    const fetchData = useBoundCallback(
        filters =>
            fetchConsentReportsDashboard({
                filters: {
                    ...filters,
                    requestedCharts: ['consentsOverTime'],
                },
            }),
        [filters]
    );

    const helpers = useMemo(
        () => ({
            makeCSV,
            name: 'opt-in-vs-opt-out',
        }),
        []
    );
    const makeDownload = useBoundCallback(makeDownloadBlob, [echartsInstance, data, helpers]);

    useLayoutEffect(() => {
        providedData ? load(Promise.resolve(providedData)) : load(fetchData());
    }, [providedData]);

    return (
        <WidgetContainer
            disabled={noResults}
            downloadFormats={['csv', 'png']}
            echartsInstance={echartsInstance}
            error={error}
            title="consent.dashboard.charts.title.optInVsOut"
            tooltip="consent.dashboard.charts.tooltip.optInVsOut"
            translationOptions={{ category: `consent.categories.${category}` }}
            actions={
                <Stack
                    direction="row"
                    alignItems="flex-end"
                    justifyContent="stretch"
                    sx={{ mx: 1, mb: 1, mt: -1, width: 'auto' }}
                >
                    <Select
                        value={category}
                        onChange={e => setCategory(/** @type {ConsentCategory} */ (e.target.value))}
                        size="small"
                        fullWidth
                    >
                        <MenuItem value={CMP_CONSENT_CATEGORIES.OPT_OUT}>
                            <Locale path={`consent.categories.${CMP_CONSENT_CATEGORIES.OPT_OUT}`} />
                        </MenuItem>
                        <MenuItem value={CMP_CONSENT_CATEGORIES.ANALYTICS}>
                            <Locale
                                path={`consent.categories.${CMP_CONSENT_CATEGORIES.ANALYTICS}`}
                            />
                        </MenuItem>
                        <MenuItem value={CMP_CONSENT_CATEGORIES.PERSONALIZATION}>
                            <Locale
                                path={`consent.categories.${CMP_CONSENT_CATEGORIES.PERSONALIZATION}`}
                            />
                        </MenuItem>
                        <MenuItem value={CMP_CONSENT_CATEGORIES.MARKETING}>
                            <Locale
                                path={`consent.categories.${CMP_CONSENT_CATEGORIES.MARKETING}`}
                            />
                        </MenuItem>
                    </Select>
                </Stack>
            }
        >
            <EChart
                onChartReady={onChartReady}
                option={memoOption}
                loading={isLoading}
                noResults={noResults}
                makeDownload={makeDownload}
            />
        </WidgetContainer>
    );
};

OptInOut.propTypes = {
    data: PropTypes.object,
    filters: PropTypes.object,
    loading: PropTypes.bool,
    shouldRetranslate: PropTypes.string,
};

/** @type {import('/b2b/common/hoc/withAsyncData').ComponentWithOverriddenProps<CmpReportsData, { filters: CmpReportsQueryParams }>} */
const TranslatedComponent = withRetranslate(OptInOut);

export default withAsyncData(TranslatedComponent, ConsentDashboardContext);
